// Admin Demo Guide page strings — EN + TR.
//
// Wave-3 Day 5. DemoGuide.tsx (377 LOC). Wave-3 biggest TR-only narrative
// sweep — ~120 strings translated:
// - Page chrome (title / hero paragraph / role-hint help box / footer note)
// - 8 scenario titles + 8 scenario narratives (1-3 paragraph each)
// - 35+ step instructions across the 8 scenarios
// - Per-step role-hint label prefix ("rol:" → "role:")
//
// Karakter proper noun preserve (data-driven, not translated):
// - Sarah Johnson BO2 Reseller (Austin TX)
// - Mehmet Üretim Ortağı SO2 (Bursa)
// - Hakan SO2 (Bursa)
// - Aybars Ahşap SO3 (Vendor)
// - Hans Müller BO3 (Berlin volume distributor)
// - Cengiz BO3 (B2B)
// - Volkan A1 (Platform Owner)
//
// Volkan ratify (C) 2026-06-02 ardışık dil-tamamen-bitsin → DemoGuide
// bilingual ratify (audit notu "may be worth flagging as demo/internal —
// EN only acceptable for V1" override).
//
// Source: features/admin/DemoGuide.tsx.

import type { LocalizedDict } from '../index'

type ScenarioKey =
  | 'meta_reset'
  | 'iq1_aybars_approve'
  | 'iq1_cross_side'
  | 'iq2_aybars_exit'
  | 'iq7_assortment'
  | 'wo_mehmet'
  | 'feedback_sarah'

type ScenarioCopy = {
  title: string
  narrative: string
  steps: string[] // index-matched against SCENARIOS step order
  linkLabels: string[] // index-matched against SCENARIOS step links (sparse — empty string if no link at that index)
}

type DemoGuideStrings = {
  page: {
    title: string
    description: string
    helpBox: string
  }
  stepRoleLabel: (role: string) => string
  footer: {
    title: string
    body: string
  }
  scenarios: Record<ScenarioKey, ScenarioCopy>
}

export const demoGuideI18n: LocalizedDict<DemoGuideStrings> = {
  en: {
    page: {
      title: 'Demo Guide',
      description:
        'PEXIFY M0 operational simulation walkthrough. 7 scenarios + 1 meta — work through them in order or show them to someone. Every scenario simulates a real operational flow on top of character-driven mock data.',
      helpBox:
        '💡 Some steps require persona/role switching (Topbar dropdown + Supply persona switcher). Those are flagged with a blue tag. Direct links are clickable immediately.',
    },
    stepRoleLabel: (role) => `role: ${role}`,
    footer: {
      title: 'Side note',
      body:
        'M0 operational simulation; every flow runs in the browser via MSW intercepts. The real backend comes online at the start of M3 (FastAPI + PostgreSQL). State is in-memory for now; a page refresh / reset clears it. Per-folder CLAUDE.md and the planning/ folder are the source-of-truth for architectural decisions.',
    },
    scenarios: {
      meta_reset: {
        title: 'Clean start — demo state reset',
        narrative:
          "Before walking through the scenarios in order, reset everything to the initial snapshot. Earlier clicks (you may have rejected Aybars, moved Mehmet's WO to shipped) get cleared.",
        steps: [
          'On the Admin home, click the "↻ Reset Demo State" button under Operations Snapshot. Confirm.',
        ],
        linkLabels: ['Admin Home → Reset'],
      },
      iq1_aybars_approve: {
        title: "IQ-1: Approve Aybars's 15% price increase",
        narrative:
          'Aybars Ahşap (SO3 vendor) suggests bumping the master catalog price of "Laser-engraved Large Wooden Cheese Board" from $28.50 → $32.78 (15% increase). Volkan A1 sees it in the admin queue and approves or rejects.',
        steps: [
          'Admin sidebar → Pending Updates (IQ-1). Find Aybars\'s "vus_a1b2c3" row.',
          'Click the row → detail page. Old ($28.50) vs new ($32.78) comparison + Aybars\'s rationale.',
          'In the Actions block, click "Approve" or "Reject". Reject opens a rationale textarea.',
          'Go back to the pending-updates list → see the status change (approved/rejected badge).',
        ],
        linkLabels: [
          'Pending Updates Queue',
          "Aybars's 15% increase detail",
          '',
          'Back to list',
        ],
      },
      iq1_cross_side: {
        title: 'IQ-1 cross-side: Aybars submits a new suggestion + admin approves',
        narrative:
          'Aybars submits a new master catalog update suggestion from the supply side. Admin sees it in the queue and approves it. Aybars sees the status change on his side. Operational proof of D-3 buy/supply split + D-5 Ring 2 cross-side mediation.',
        steps: [
          'Topbar → Acting as: Supply. The persona dropdown should have Aybars Ahşap (SO3) selected.',
          'Supply sidebar → Submit New Update. Fill in the form (e.g. prod_test, "Test product", price_usd 10 → 12, rationale). Send.',
          "You're redirected to My Submitted Updates. The new suggestion appears with pending status.",
          'Topbar → Acting as: Admin. Pending Updates → your suggestion is in the queue.',
          'Approve → switch back to Supply → My Submitted → status changed (approved).',
        ],
        linkLabels: [
          '',
          'Submit New Update form',
          'My Submitted Updates',
          'Switch to admin queue',
          '',
        ],
      },
      iq2_aybars_exit: {
        title: 'IQ-2: Aybars exit replacement workflow (4-state)',
        narrative:
          'Aybars is closing his workshop at the end of Q2 — 3 wooden products are affected. Admin assigns an alternative vendor (Mehmet, Hakan, or New Wood Workshop), starts the drain period, then closes the workflow. State machine: exit_requested → alternative_pending → switched → drained (terminal).',
        steps: [
          'Admin sidebar → Vendor Replacements (IQ-2). The vr_001 "Aybars Ahşap" row is visible.',
          'Open it → detail page. State: exit_requested.',
          '"Pick alternative vendor" dropdown → Mehmet Üretim Ortağı (SO2) → "Assign alternative".',
          'State → alternative_pending. Now "Drain period (days)" → 14 (default) → "Switched (start drain)".',
          'State → switched. drain_until is set (14 days from now). "Drained (close)" button appears.',
          '"Drained (close)" → terminal state. Workflow closed; admin notes auto-appended.',
        ],
        linkLabels: [
          'Vendor Replacements list',
          'vr_001 detail',
          '',
          '',
          '',
          '',
        ],
      },
      iq7_assortment: {
        title: 'IQ-7: Assortment override (program-tier matrix)',
        narrative:
          'Admin makes 7 products visible or hidden across 4 program tiers (BO2 Standard / BO2 Premium / BO3 Standard / BO3 Premium). Default tier visibility is category-based; admin can override per product. Per-seller overrides are V2+; V1 covers program-tier only.',
        steps: [
          'Admin sidebar → Assortment (IQ-7). 7 product × 4 tier matrix table.',
          'Click a cell (e.g. "Laser-engraved Large Wooden" × "BO3 Premium"). The visibility toggles. If it diverges from the default, an "override" badge appears.',
          'In the same row, "back to default" clears the override and reverts to default tier visibility.',
          'Filter dropdown → "Overridden only" → products where admin has manually intervened.',
        ],
        linkLabels: ['Assortment matrix', '', '', ''],
      },
      wo_mehmet: {
        title: 'Work Order: Mehmet 5-state production flow',
        narrative:
          'Mehmet (SO2 production partner) sees his own work orders and advances their state. State machine: assigned → in_production → quality_check → shipped → completed (terminal). Each transition is recorded with an optional note in the lifecycle history; admin sees it in the activity feed.',
        steps: [
          'Topbar → Acting as: Supply. From the persona switcher, pick Mehmet Üretim Ortağı (SO2).',
          "Supply sidebar → My Work Orders. Mehmet's 4 WOs are listed.",
          'Click the wo_2026_0145 row (in_production state) → detail.',
          'State-machine visual (assigned → in_production current → ...). Transition-note textarea + "Send to QC" button.',
          'Topbar → Admin → activity feed shows the "Mehmet Üretim Ortağı transitioned: …" entry (5s refetch).',
        ],
        linkLabels: [
          '',
          "Mehmet's WOs",
          'wo_2026_0145 detail',
          '',
          'Admin home (activity feed)',
        ],
      },
      feedback_sarah: {
        title: 'Catalog Issue: Sarah BO2 feedback flow',
        narrative:
          'Sarah BO2 reseller spots a problem in the master catalog while looking at her storefront (missing description, wrong stock, bad photo, etc.). She reports it to admin via "Report issue". Admin sees it in the queue, investigates, writes a resolution. State machine: open → investigating → resolved/dismissed.',
        steps: [
          'Topbar → Acting as: Seller (Sarah BO2 Reseller).',
          'Seller sidebar → My Products. Every row has a "Report issue" link.',
          'Click "Report issue" on a product → form prefilled (product name + ID). Pick a category (Description / Image / Stock / etc.), write a description, send.',
          'You\'re redirected to My Reports. The new report appears in "Open" status.',
          'Topbar → Admin → sidebar → Catalog Issues. Sarah\'s reported queue.',
          'Inline buttons: "Start investigating" / "Resolved" (response textarea) / "Close (dismiss)". Write a resolution and save.',
          'Topbar → Seller → My Reports → status changed (Resolved) + admin response visible.',
        ],
        linkLabels: [
          '',
          'My Products',
          '',
          'My Reports',
          'Admin Catalog Issues queue',
          '',
          '',
        ],
      },
    },
  },
  tr: {
    page: {
      title: 'Demo Rehberi',
      description:
        'PEXIFY M0 operational simulation walkthrough. 7 senaryo + 1 meta — sırayla deneyimle veya başkasına göster. Her senaryo karakter-driven mock data üzerinden gerçek bir operasyonel akışı simüle eder.',
      helpBox:
        '💡 Bazı adımlar persona/role değişimi gerektirir (Topbar dropdown + Supply persona switcher). Bunlar mavi etiketle işaretli. Direkt linkler hemen tıklanabilir.',
    },
    stepRoleLabel: (role) => `rol: ${role}`,
    footer: {
      title: 'Ek not',
      body:
        'M0 operational simulation; tüm akışlar browser-side MSW intercept ile çalışır. Gerçek backend M3 başlangıcında devreye girer (FastAPI + PostgreSQL). Şu anki state in-memory; sayfa yenilemesi/reset ile temizlenir. Per-folder CLAUDE.md ve planning/ klasörü mimari kararların source-of-truth.',
    },
    scenarios: {
      meta_reset: {
        title: 'Temiz başlangıç — demo state reset',
        narrative:
          "Senaryoları sırayla deneyimletmeden önce her şeyi initial snapshot'a sıfırla. Önceki tıklamalar (Aybars'ı reddetmiş olabilirsin, Mehmet'in WO'sunu shipped'a almış olabilirsin) temizlenir.",
        steps: [
          'Admin home\'da Operations Snapshot altındaki "↻ Reset Demo State" butonuna tıkla. Onay ver.',
        ],
        linkLabels: ['Admin Home → Reset'],
      },
      iq1_aybars_approve: {
        title: "IQ-1: Aybars'ın %15 zam talebini onayla",
        narrative:
          'Aybars Ahşap SO3 tedarikçi "Lazerli Büyük Ahşap Peynir Tahtası" için master katalog fiyatını $28.50 → $32.78 (%15 zam) öneriyor. Volkan A1 admin queue\'da görür ve onay/red kararı verir.',
        steps: [
          'Admin sidebar → Pending Updates (IQ-1). Aybars\'ın "vus_a1b2c3" satırını gör.',
          'Satıra tıkla → detail page. Eski ($28.50) vs yeni ($32.78) karşılaştırması + Aybars\'ın gerekçesi.',
          'Aksiyonlar bölümünde "Onayla" veya "Reddet" tıkla. Reddedersen gerekçe textarea\'sı açılır.',
          'Geri pending-updates listesine dön → status değişimini gör (badge approved/rejected).',
        ],
        linkLabels: [
          'Pending Updates Queue',
          'Aybars %15 zam detayı',
          '',
          'Listeye dön',
        ],
      },
      iq1_cross_side: {
        title: 'IQ-1 cross-side: Aybars yeni öneri gönderir + admin onaylar',
        narrative:
          "Aybars supply tarafında yeni master katalog güncelleme önerisi gönderir. Admin queue'da görür, onaylar. Aybars status değişimini kendi tarafında görür. D-3 buy/supply split + D-5 Ring 2 cross-side mediation operasyonel kanıt.",
        steps: [
          "Topbar → Acting as: Supply. Persona dropdown'unda Aybars Ahşap (SO3) seçili olsun.",
          'Supply sidebar → Submit New Update. Form doldur (örn: prod_test, "Test ürün", price_usd 10 → 12, gerekçe). Gönder.',
          "My Submitted Updates'e geri yönlendirilirsin. Yeni öneri pending status'unda görünür.",
          'Topbar → Acting as: Admin. Pending Updates → senin gönderdiğin öneri kuyrukta.',
          'Onayla → Supply\'a geri dön → My Submitted → status değişti (approved).',
        ],
        linkLabels: [
          '',
          'Submit New Update form',
          'My Submitted Updates',
          "Admin queue'ya geç",
          '',
        ],
      },
      iq2_aybars_exit: {
        title: 'IQ-2: Aybars exit replacement workflow (4-state)',
        narrative:
          "Aybars Q2 sonu işyerini kapatıyor — 3 ahşap ürün etkilenecek. Admin alternatif tedarikçi (Mehmet, Hakan, veya Yeni Ahşap Atölyesi) atar, drain period başlatır, sonra workflow'u kapatır. State machine: exit_requested → alternative_pending → switched → drained (terminal).",
        steps: [
          'Admin sidebar → Vendor Replacements (IQ-2). vr_001 "Aybars Ahşap" satırı görünür.',
          'Aç → detail page. State: exit_requested.',
          '"Alternatif tedarikçi seç" dropdown → Mehmet Üretim Ortağı (SO2) → "Alternatifi ata".',
          'State → alternative_pending. Şimdi "Drain süresi (gün)" → 14 (default) → "Switched (drain başlat)".',
          'State → switched. drain_until set edilir (14 gün sonrası). "Drained (kapat)" butonu görünür.',
          '"Drained (kapat)" → terminal state. Workflow kapandı; admin notları otomatik append edilir.',
        ],
        linkLabels: [
          'Vendor Replacements list',
          'vr_001 detayı',
          '',
          '',
          '',
          '',
        ],
      },
      iq7_assortment: {
        title: 'IQ-7: Assortment override (program-tier matrix)',
        narrative:
          "Admin 7 ürünü 4 program tier'a (BO2 Standard / BO2 Premium / BO3 Standard / BO3 Premium) görünür kılar veya gizler. Default tier görünürlüğü kategori bazlıdır; admin her ürün için override yapabilir. Per-seller override V2+; V1'de sadece program-tier.",
        steps: [
          'Admin sidebar → Assortment (IQ-7). 7 ürün × 4 tier matrix tablosu.',
          'Bir hücreye tıkla (örn: "Lazerli Büyük Ahşap" × "BO3 Premium"). Görünürlük toggle olur. Default\'tan farklılaşırsa "override" badge çıkar.',
          'Aynı satırda "default\'a dön" linki → override temizlenir, default tier görünürlüğüne döner.',
          'Filtre dropdown → "Sadece override edilmiş" → admin\'in manuel müdahale ettiği ürünler.',
        ],
        linkLabels: ['Assortment matrix', '', '', ''],
      },
      wo_mehmet: {
        title: 'Work Order: Mehmet 5-state üretim akışı',
        narrative:
          "Mehmet SO2 Production Partner kendi iş emirlerini görür ve state'ini ilerletir. State machine: assigned → in_production → quality_check → shipped → completed (terminal). Her geçiş opsiyonel not ile lifecycle history'sine kaydedilir; admin activity feed'de görür.",
        steps: [
          "Topbar → Acting as: Supply. Persona switcher'dan Mehmet Üretim Ortağı (SO2) seç.",
          "Supply sidebar → My Work Orders. Mehmet'in 4 WO'su listelenir.",
          'wo_2026_0145 (in_production state) satırına tıkla → detail.',
          'State machine görseli (assigned → in_production current → ...). Geçiş notu textarea + "QC\'ye gönder" butonu.',
          'Topbar → Admin → activity feed\'de "Mehmet Üretim Ortağı transitioned: ..." satırı görünür (5s refetch).',
        ],
        linkLabels: [
          '',
          "Mehmet'in WO'ları",
          'wo_2026_0145 detayı',
          '',
          'Admin home (activity feed)',
        ],
      },
      feedback_sarah: {
        title: 'Catalog Issue: Sarah BO2 feedback flow',
        narrative:
          "Sarah BO2 reseller storefront'unda master kataloğa dair bir sorun fark eder (eksik açıklama, yanlış stok, kötü fotoğraf vb.). \"Sorun bildir\" ile admin'e iletir. Admin queue'da görür, incelemeye alır, çözüm yazar. State machine: open → investigating → resolved/dismissed.",
        steps: [
          'Topbar → Acting as: Seller (Sarah BO2 Reseller).',
          'Seller sidebar → My Products. Her satırda "Sorun bildir" linki var.',
          'Bir ürünün "Sorun bildir" linkine tıkla → form prefilled (ürün adı + ID). Kategori seç (Açıklama / Görsel / Stok / vb.), açıklama yaz, gönder.',
          "My Reports'a yönlendirilirsin. Yeni rapor \"Açık\" status'unda görünür.",
          "Topbar → Admin → sidebar → Catalog Issues. Sarah'nın bildirdiği queue.",
          'İnline butonlar: "İncelemeye al" / "Çözüldü" (response textarea) / "Kapat (dismiss)". Çözüm yazıp kaydet.',
          'Topbar → Seller → My Reports → status değişti (Çözüldü) + admin yanıtı görünür.',
        ],
        linkLabels: [
          '',
          'My Products',
          '',
          'My Reports',
          'Admin Catalog Issues queue',
          '',
          '',
        ],
      },
    },
  },
}
